import React from 'react';
import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';

import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { Text } from '@rover/kibble/core';
import { AvailabilityPreferences } from '@rover/rsdk/src/apiClient/latest/model/availabilityPreferences';

type Props = {
  sitterName: string;
  capacity: AvailabilityPreferences;
  serviceType?: string;
  textSize?: '100' | '200';
};

const ServiceCapacity = ({
  sitterName,
  capacity,
  serviceType,
  textSize = '100',
}: Props): JSX.Element => {
  const { t } = useLingui();
  const count = capacity.spacesAvailable;

  const getText = (): string => {
    // These services' capacity counts bookings or visits, not pets.
    switch (serviceType) {
      case ServiceType.OVERNIGHT_TRAVELING:
        return t({
          message: plural(count, {
            one: `${sitterName} can take # House Sitting booking per night`,
            other: `${sitterName} can take up to # House Sitting bookings per night`,
          }),
        });
      case ServiceType.DROP_IN:
        return t({
          message: plural(count, {
            one: `${sitterName} can take up to # Drop-in Visit per day`,
            other: `${sitterName} can take up to # Drop-in Visits per day`,
          }),
        });
      case ServiceType.DOG_WALKING:
        return t({
          message: plural(count, {
            one: `${sitterName} can take up to # Walk per day`,
            other: `${sitterName} can take up to # Walks per day`,
          }),
        });
      default: {
        const spacesAvailableText =
          count === 1 ? capacity.spacesAvailableTextSingular : capacity.spacesAvailableTextPlural;
        const label = spacesAvailableText.toLowerCase();
        return t`${sitterName} can host up to ${count} ${label}`;
      }
    }
  };

  return <Text size={textSize}>{getText()}</Text>;
};

export default ServiceCapacity;
